import { useState, useEffect } from "react";
import generarLineaCaptura from "../utilities/GenerarLineaCaptura";
import { generarFolioTPV, construirTablaPdf, descargarOrdenDePago } from "../utilities/ordenDePago";
import { plantelOficialDesdeIdBD } from "../utilities/plantel";
import { API_BASE, PINPAD_URL } from "../config";
import { asegurarVersionVigente } from "../utilities/versionApp";
import { apiFetch } from "../utilities/apiFetch";

const MAX_ITEMS = 5;

// user: { id_plantel, id_usuario, nombre_plantel } de la sesion del cajero.
// catalogoConceptos: el catalogo completo que ya carga DashboardCajero
// (se filtra aqui por ex_alumno === true, sin pedirlo de nuevo al backend).
const useCobroExAlumnos = (user, catalogoConceptos) => {
  const [referencia, setReferencia] = useState(null); // { matricula, nombre }
  const [cargandoReferencia, setCargandoReferencia] = useState(true);
  const [carrito, setCarrito] = useState([]);
  const [nombreSolicitante, setNombreSolicitante] = useState("");
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [modalExito, setModalExito] = useState({
    visible: false, nombre: "", monto: 0, autorizacion: "", referencia: ""
  });

  const catalogo = (catalogoConceptos || []).filter((c) => c.ex_alumno);
  const totalCarrito = carrito.reduce((sum, item) => sum + item.precio * item.cantidad, 0);

  useEffect(() => {
    const fetchReferencia = async () => {
      setCargandoReferencia(true);
      try {
        const res = await apiFetch(`${API_BASE}/wMultipagosObtenerExAlumnos.php?id_plantel=${user?.id_plantel}`);
        const data = await res.json();
        if (data.success) {
          setReferencia(data.data);
        } else {
          setReferencia(null);
          mostrarNotificacion("error", data.message || "No hay una referencia de ex-alumnos configurada para este plantel.");
        }
      } catch (error) {
        setReferencia(null);
        mostrarNotificacion("error", "No se pudo conectar con el servidor.");
      } finally {
        setCargandoReferencia(false);
      }
    };
    if (user?.id_plantel) fetchReferencia();
  }, [user?.id_plantel]);

  const mostrarNotificacion = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: "", texto: "" }), 5000);
  };

  const agregarAlCarrito = (concepto) => {
    setCarrito((prev) => {
      const totalItems = prev.reduce((sum, item) => sum + item.cantidad, 0);
      if (totalItems >= MAX_ITEMS) {
        mostrarNotificacion("error", `Solo puedes cobrar hasta ${MAX_ITEMS} conceptos por transaccion.`);
        return prev;
      }
      const index = prev.findIndex((item) => item.id === concepto.id);
      if (index !== -1) {
        const nuevo = [...prev];
        nuevo[index] = { ...nuevo[index], cantidad: nuevo[index].cantidad + 1 };
        return nuevo;
      }
      return [...prev, { ...concepto, cantidad: 1 }];
    });
  };

  const quitarDelCarrito = (conceptoId) => {
    setCarrito((prev) => {
      const index = prev.findIndex((item) => item.id === conceptoId);
      if (index === -1) return prev;
      const item = prev[index];
      if (item.cantidad > 1) {
        const nuevo = [...prev];
        nuevo[index] = { ...item, cantidad: item.cantidad - 1 };
        return nuevo;
      }
      return prev.filter((i) => i.id !== conceptoId);
    });
  };

  const limpiarCarrito = () => {
    setCarrito([]);
    setNombreSolicitante("");
  };

  // Claves repetidas segun cantidad: para calcular la linea de captura
  // (generarLineaCaptura espera un arreglo con una clave por unidad).
  const construirClaves = () => {
    const claves = [];
    carrito.forEach((item) => {
      for (let i = 0; i < item.cantidad; i++) claves.push(item.clave);
    });
    return claves;
  };

  // Conceptos "aplanados" (una fila por unidad): Mtd_GuardarIntencion inserta
  // un detalle por cada elemento del arreglo que recibe, sin leer "cantidad"
  // (a diferencia de Mtd_GuardarPagoCaja, que si la expande el mismo).
  const construirConceptosPlano = () => {
    const lista = [];
    carrito.forEach((item) => {
      for (let i = 0; i < item.cantidad; i++) {
        lista.push({ clave: item.clave, concepto: item.concepto, precio: item.precio });
      }
    });
    return lista;
  };

  const validarAntesDeCobrar = () => {
    if (carrito.length === 0 || !referencia) return false;
    if (!nombreSolicitante.trim()) {
      mostrarNotificacion("error", "Captura el nombre de quien solicita el documento.");
      return false;
    }
    return true;
  };

  const procesarCobroPinpad = async () => {
    if (!validarAntesDeCobrar()) return;
    setLoading(true);
    try {
      const idPlantelBanco = plantelOficialDesdeIdBD(user?.id_plantel);
      const claves = construirClaves();
      const infoCaptura = generarLineaCaptura(idPlantelBanco, claves, referencia.matricula, totalCarrito);

      const referenciaBase26 = infoCaptura.banorte.referenciaBase.substring(0, 26);
      const sufijoTiempo = Date.now().toString().slice(-4);
      const referenciaTerminal = referenciaBase26 + sufijoTiempo; // 30 caracteres

      // Antes de pasar la tarjeta: si hay una version nueva publicada, no cobrar
      if (!(await asegurarVersionVigente())) return;

      const resPinpad = await fetch(PINPAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Monto: totalCarrito, Referencia: referenciaTerminal, Concepto: "PAGO EX-ALUMNO" })
      });
      const dataPinpad = await resPinpad.json();

      if (dataPinpad.status !== "Aprobado") {
        mostrarNotificacion("error", `Terminal: ${dataPinpad.mensaje}`);
        return;
      }

      const payloadPago = {
        alumno: referencia.matricula,
        cajero: user?.id_usuario,
        plantel: user?.id_plantel,
        total: totalCarrito,
        detalle: carrito, // aqui SI va con "cantidad": el backend la expande solo
        lineaCapturaFinal: referenciaTerminal,
        referenciaBase: referenciaBase26,
        fechaVencimiento: "",
        autorizacion: dataPinpad.autorizacion,
        exalumno: nombreSolicitante.trim()
      };

      const resPago = await apiFetch(`${API_BASE}/wMultipagosGuardarPagoCaja.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadPago)
      });
      const dataPago = await resPago.json();

      if (dataPago.success) {
        setModalExito({
          visible: true,
          nombre: nombreSolicitante.trim(),
          monto: totalCarrito,
          autorizacion: dataPinpad.autorizacion,
          referencia: referenciaTerminal
        });
        limpiarCarrito();
      } else {
        mostrarNotificacion("error", "Cobro guardado con errores: " + (dataPago.message || ""));
      }
    } catch (error) {
      mostrarNotificacion("error", "Error con la Pinpad.");
    } finally {
      setLoading(false);
    }
  };

  const procesarCobroVentanilla = async () => {
    if (!validarAntesDeCobrar()) return;
    setLoading(true);
    try {
      const idPlantelBanco = plantelOficialDesdeIdBD(user?.id_plantel);
      const claves = construirClaves();
      const infoCaptura = generarLineaCaptura(idPlantelBanco, claves, referencia.matricula, totalCarrito);
      const nuevoFolioTPV = generarFolioTPV(user?.id_plantel);

      const payloadIntencion = {
        codigo_alumno: referencia.matricula,
        referencia_banorte: infoCaptura.banorte.lineaDeCaptura,
        referencia_bbva: infoCaptura.bancomer.lineaDeCaptura,
        importe: totalCarrito,
        id_plantel: user?.id_plantel,
        id_cajero: user?.id_usuario,
        conceptos: construirConceptosPlano(), // aqui SIN "cantidad": ya viene aplanado
        metodo_esperado: "VENTANILLA",
        folio_registro: nuevoFolioTPV,
        exalumno: nombreSolicitante.trim()
      };

      const resInt = await fetch(`${API_BASE}/wMultipagosGuardarIntencion.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadIntencion)
      });
      const dataInt = await resInt.json();

      if (dataInt.success) {
        mostrarNotificacion("success", "Generando ficha...");

        await descargarOrdenDePago(
          {
            name: nombreSolicitante.trim(),
            code: referencia.matricula,
            table: construirTablaPdf(carrito, infoCaptura, nuevoFolioTPV),
            fechaVencimiento: infoCaptura.banorte.fechaVencimientoStr,
            plantelOverride: idPlantelBanco,
            nombrePlantelOverride: user?.nombre_plantel,
            mostrarMatricula: false,
          },
          "Orden de pago.pdf"
        );

        limpiarCarrito();
      } else {
        mostrarNotificacion("error", dataInt.message);
      }
    } catch (error) {
      mostrarNotificacion("error", "Error al procesar ventanilla.");
    } finally {
      setLoading(false);
    }
  };

  return {
    catalogo,
    referencia,
    cargandoReferencia,
    carrito,
    agregarAlCarrito,
    quitarDelCarrito,
    totalCarrito,
    nombreSolicitante,
    setNombreSolicitante,
    loading,
    mensaje,
    modalExito,
    setModalExito,
    procesarCobroPinpad,
    procesarCobroVentanilla
  };
};

export default useCobroExAlumnos;
